import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, FolderKanban, Settings, LogOut, Globe, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useModule } from "@/context/ModuleContext";
import { Logo } from "@/components/Logo";

export default function Sidebar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { meta } = useModule();
  const navigate = useNavigate();

  // Primary nav — Analyzer-first framing. Estimator-only routes (/electrical,
  // /prices) are hidden from the menu but still reachable by URL.
  const items = [
    { to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/projects", label: t("nav.projects"), icon: FolderKanban, testid: "nav-projects" },
    { to: "/references", label: t("nav.references"), icon: BookOpen, testid: "nav-references" },
    { to: "/settings", label: t("nav.settings"), icon: Settings, testid: "nav-settings" },
  ];

  const toggleLang = () => {
    const next = i18n.language?.startsWith("es") ? "en" : "es";
    i18n.changeLanguage(next);
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside className="w-60 shrink-0 bg-white border-r border-slate-200 h-screen sticky top-0 flex flex-col" data-testid="sidebar">
      <div className="p-4 border-b border-slate-200">
        <Logo />
        <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] flex items-center gap-1.5"
             style={{ color: meta.color }}>
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
          {i18n.language?.startsWith("es") ? meta.labelEs : meta.labelEn}
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
                isActive
                  ? "bg-[#003366] text-white"
                  : "text-slate-700 hover:bg-slate-100"
              }`
            }
          >
            <it.icon size={16} strokeWidth={1.75} />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200 space-y-2">
        <button
          onClick={toggleLang}
          data-testid="toggle-language-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-mono uppercase tracking-wider text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <Globe size={14} strokeWidth={1.75} />
          <span>{i18n.language?.startsWith("es") ? "ES · Español" : "EN · English"}</span>
        </button>
        {user && (
          <div className="px-3 py-2 text-xs">
            <div className="font-medium text-slate-900 truncate" data-testid="user-name">{user.full_name}</div>
            <div className="font-mono uppercase tracking-wider text-slate-500">{user.role}</div>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-colors"
        >
          <LogOut size={14} strokeWidth={1.75} />
          <span>{t("nav.logout")}</span>
        </button>
      </div>
    </aside>
  );
}
